import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import {
  EndorsementServiceFeaturesGetOwnEndorsementsDispensationResponse as DispensationDto,
  EndorsementServiceFeaturesGetOwnEndorsementsGetOwnEndorsementsDtoEndorsement as EndorsementDto,
} from '@sdir/httpclient/lib/clients/aps/endorsement';
import { AlertSingleLine, Loader, SdirModal, styles, TextLink } from '@sdir/sds';
import { isToggleOn } from '@sdir/blueprint.aps/lib/helpers/permissionUtilities';
import { Dispensation, Endorsement } from '@sdir/blueprint.aps/lib/components';
import { PageTitle } from '../../Atoms';
import { ToggleEndorsement } from '../../../constants/permissions';
import { useFeatureToggles, useOwnEndorsements } from '../../../services/hooks';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';
import { routes } from '../../../constants/routes';
import { EndorsementTile } from '../../Molecules/EndorsementTile';

const EndorsementPage: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const featureToggle = useFeatureToggles();
  const { ownEndorsements } = useOwnEndorsements();
  const [selectedEndorsement, setSelectedEndorsement] = useState<EndorsementDto>();
  const [selectedDispensation, setSelectedDispensation] = useState<DispensationDto>();
  const [showEndorsement, setShowEndorsement] = useState<boolean>(false);
  const [showDispensation, setShowDispensation] = useState<boolean>(false);
  const showEndorsements = !isToggleOn(featureToggle?.result, ToggleEndorsement, '');

  const backToDashboard = useCallback(() => {
    navigate(routes.home);
  }, [navigate]);

  const onOpenEndorsement = (endorsement: EndorsementDto) => {
    setSelectedEndorsement(endorsement);
    setShowEndorsement(true);
  };
  const onCloseEndorsement = () => {
    setSelectedEndorsement(undefined);
    setShowEndorsement(false);
  };

  const onOpenDispensation = (dispensation: DispensationDto) => {
    setSelectedDispensation(dispensation);
    setShowDispensation(true);
  };
  const onCloseDispensation = () => {
    setSelectedDispensation(undefined);
    setShowDispensation(false);
  };

  if (!showEndorsements) {
    return (
      <AlertSingleLine
        title="Error"
        type="error"
        text={intl.formatMessage({ id: 'utils.errors.insufficientaccess' })}
      />
    );
  }

  const hasEndorsements = ownEndorsements?.result && ownEndorsements?.result?.length > 0;

  return (
    <Wrapper>
      <PageSubheader>
        <PageTitle title={intl.formatMessage({ id: 'endorsements.page.title' })} />
        <TextLink
          size="2rem"
          text={intl.formatMessage({ id: 'back.to.dashboard' })}
          click={backToDashboard}
        />
      </PageSubheader>
      <H2 className="sectionheader">{intl.formatMessage({ id: 'endorsements.page.subtitle1' })}</H2>
      <TileContainer>
        {ownEndorsements?.loading && <Loader />}
        {ownEndorsements?.result?.map((e) => {
          return (
            <EndorsementTile
              data={e}
              onOpenEndorsement={onOpenEndorsement}
              onOpenDispensation={onOpenDispensation}
            />
          );
        })}
        {!ownEndorsements?.loading && !ownEndorsements?.error && !hasEndorsements && (
          <EmptyListMessage>
            {intl.formatMessage({ id: 'endorsements.noendorsements' })}
          </EmptyListMessage>
        )}
      </TileContainer>
      <StyledModal visible={showDispensation} onCancel={onCloseDispensation}>
        <div translate="no">
          <Dispensation
            applicationDate={selectedDispensation?.issuedAt || ''}
            citizenship={selectedDispensation?.seafarerNationality || ''}
            dateOfBirth={selectedDispensation?.seafarerDateOfBirth || ''}
            name={selectedDispensation?.seafarerName || ''}
            validDate={selectedDispensation?.expiresAt || ''}
          />
        </div>
      </StyledModal>
      <StyledModal visible={showEndorsement} onCancel={onCloseEndorsement}>
        <div translate="no">
          <Endorsement
            authority={selectedEndorsement?.nationalAuthority || ''}
            certificateNr={selectedEndorsement?.certificateNumber || ''}
            endorsementNr={selectedEndorsement?.endorsementNumber || ''}
            dateOfBirth={selectedEndorsement?.seafarerDateOfBirth || ''}
            name={selectedEndorsement?.seafarerName || ''}
            positionList={[
              {
                position: selectedEndorsement?.title || '',
                limitations: [intl.formatMessage({ id: 'endorsements.page.limitation' })],
              },
            ]}
            validDate={selectedEndorsement?.expiresAt || ''}
            isStcwF={selectedEndorsement?.regulationReference === 'STCW_F'}
            issuedDate={selectedEndorsement?.issuedAt}
            qrImageLoading={false}
            // eslint-disable-next-line max-len
            qrData={`${process.env.REACT_APP_VERIFY_URL}/en/endorsement/${selectedEndorsement?.endorsementNumber}`}
            hideLanguageSelector
          />
        </div>
      </StyledModal>
    </Wrapper>
  );
};

const StyledModal = styled(SdirModal)`
  p {
    margin: 0;
  }
`;

const EmptyListMessage = styled.div`
  padding: 60px;
  border: 2px dashed #356485;
  border-radius: 6px;
  width: auto;
  margin: 0 auto 16px 0;
  color: ${styles.colors.primary3};
  font-size: 16px;
  font-weight: normal;
  font-family: ${styles.fonts.stfMedium};
`;

const Wrapper = styled.div`
  display: grid;
  .sectionheader {
    margin-top: 5em;
  }
  h1 {
    margin-bottom: 0;
  }
`;

const H2 = styled.h2`
  font-size: 2rem;
  color: ${styles.colors.primary3};
  font-weight: bold;
`;

const PageSubheader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding: 1em 0;
  margin-bottom: 3em;
`;

const TileContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 32px 40px;
`;

export default EndorsementPage;
